import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
  isSameMonth, isToday, isSameDay, parseISO,
} from 'date-fns';
import { es, enUS } from 'date-fns/locale';

import calendarService from '../services/calendarService';
import transactionService from '../services/transactionService';
import recurringService from '../services/recurringService';
import apiClient from '../services/api';
import { getCategoryEmoji } from '../utils/categoryEmoji';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

// Vista de calendario de flujo de efectivo: combina transacciones reales,
// ocurrencias proyectadas de ingresos/gastos recurrentes (con
// excepciones ya aplicadas por el backend), alertas de vencimiento de
// deuda, y saldo diario/proyectado — todo lo que se pidió: "todo esto y
// alertas de vencimiento de deudas".

// 1 sep 2026 (Augusto: "el calendario me está colocando una mezcla de
// dólares y soles... necesito la opción de toggle de mostrar solo en
// soles o solo en dólares. utilizando el tipo de cambio del día"): esto
// YA NO es cierto -- antes el calendario asumía que todo era soles (ver
// el comentario que había acá) y sumaba montos en dólares como si
// fueran soles sin convertir nada. Ahora el backend convierte todo a UNA
// sola moneda (vistaMoneda, 'soles' o 'dolares') usando el tipo de
// cambio del día del BCRP -- ver /calendar/data?vista=... en
// calendarService.js y app/services/exchange_rate.py. money() ya no
// asume soles, recibe el símbolo según la vista actual.
const moneyBase = (n, simbolo = 'S/') =>
  `${simbolo} ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Rosado para pagos de deuda por vencer: se distinguen así de los gastos
// normales (rojo), que son otra cosa (compras, servicios, etc).
const PINK = '#d81b60';

// 21 sep 2026 (Augusto: "tal como pasa en la sección de Transacciones,
// las cuentas y moneda y fecha seleccionada debe de quedar registrada
// hasta que el usuario haga un Reset").
//
// Mismo mecanismo que FILTROS_STORAGE_KEY en TransactionsPage.js: un
// solo objeto JSON con toda la vista. Se guarda el MES (no el día
// exacto): al volver mañana interesa seguir en septiembre, no volver al
// 21 a las tres de la tarde.
const VISTA_STORAGE_KEY = 'pcm_vista_calendario';
const leerVistaGuardada = () => {
  try {
    const raw = localStorage.getItem(VISTA_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // localStorage bloqueado (modo privado) o JSON corrupto: se arranca
    // como si no hubiera nada guardado, en vez de romper la pantalla.
    return null;
  }
};
const VISTA_GUARDADA = leerVistaGuardada();
const mesGuardado = () => {
  const iso = VISTA_GUARDADA?.mesActual;
  if (!iso) return new Date();
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? new Date() : d;
};

const CalendarPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  // 17 sep 2026 (pedido de Augusto: "se puede tener una version completa
  // en ingles?"): date-fns necesita su propio locale para nombres de mes
  // y día de la semana ("septiembre" vs "September") -- `es`/`enUS` de
  // date-fns/locale, no tiene nada que ver con LanguageContext.
  const dateLocale = isSpanish ? es : enUS;
  const diasSemanaAbrev = isSpanish
    ? ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const [mesActual, setMesActual] = useState(mesGuardado);
  const [dias, setDias] = useState({}); // fecha ISO -> objeto día
  const [balanceActual, setBalanceActual] = useState(0);
  const [hoyISO, setHoyISO] = useState(null);
  // 21 sep 2026 (Augusto: "siguen sin aparecer las transacciones
  // recurrentes, por ejemplo, Pago Daicy"): cuántas series activas no
  // tienen cuenta asignada. Al filtrar por cuenta quedan fuera -- y
  // desaparecían sin decir nada.
  const [seriesSinCuenta, setSeriesSinCuenta] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  // 10 ago 2026 (pedido de Augusto): clic en un día debe "abrir el día" --
  // antes el detalle solo aparecía en un panel debajo de toda la grilla
  // del mes, que en el celular obligaba a bajar mucho para verlo. Ahora se
  // abre en un diálogo modal (solo en vista Mes/Semana -- en vista Día el
  // detalle ya vive siempre visible debajo del día grande, un modal ahí
  // sería redundante).
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  // 4 ago 2026 (pedido de Augusto): el selector antes combinaba Cuentas Y
  // Deudas (tarjetas, préstamos) en una sola lista -- pero desde que
  // importar una tarjeta de crédito puede crear DOS Deudas con el mismo
  // nombre (una por moneda, ver _find_or_create_debt en statements.py),
  // se veían como "cuentas repetidas" en este selector sin ninguna forma
  // de distinguirlas. Augusto pidió sacar las deudas de acá por completo
  // -- el selector ahora solo filtra por Cuenta. Las alertas de
  // vencimiento de deuda (el badge rosado en cada día del calendario)
  // siguen funcionando igual, vienen del backend por separado
  // (data.alertas_deuda) y no dependen de este selector.
  const [cuentas, setCuentas] = useState([]);
  // 21 sep 2026 (Augusto: "en calendario, se necesita la opción de
  // seleccionar 1 o más cuentas"): antes era un solo valor de texto (con
  // prefijo 'acc-'). Ahora es una lista de ids. Vacía = todas las
  // cuentas, que es lo que antes significaba ''.
  const [cuentasSeleccionadas, setCuentasSeleccionadas] = useState(
    () => (Array.isArray(VISTA_GUARDADA?.cuentasSeleccionadas) ? VISTA_GUARDADA.cuentasSeleccionadas : []),
  );
  // Vista: 'mes' (grid completo, como antes), 'semana' (una sola fila de 7
  // días) o 'dia' (un solo día grande, con el detalle siempre visible
  // debajo). mesActual funciona como "fecha de referencia" para las tres.
  const [vista, setVista] = useState(() => VISTA_GUARDADA?.vista || 'mes');
  // 1 sep 2026: toggle de MONEDA (distinto de `vista` de arriba, que es
  // Mes/Semana/Día) -- 'soles' o 'dolares'. tipoCambio guarda lo que
  // devolvió el backend (fecha, compra, venta, promedio, fuente) para
  // mostrarlo siempre junto al toggle -- Augusto: "Muestra el tipo de
  // cambio y la fuente".
  const [vistaMoneda, setVistaMoneda] = useState(() => VISTA_GUARDADA?.vistaMoneda || 'soles');
  const [tipoCambio, setTipoCambio] = useState(null);
  // 4 sep 2026 (Augusto: "cuentas provisiones o ahorros intocables...
  // no incluirse en el calendario... a menos indicado por el usuario"):
  // por defecto las cuentas es_reserva quedan fuera del agregado "todas
  // las cuentas" -- este toggle las trae de vuelta. Seleccionar una
  // cuenta de reserva directo en el selector de Cuenta (de abajo) SIEMPRE
  // la muestra, sin depender de este toggle -- ver calendar.py.
  const [incluirReservas, setIncluirReservas] = useState(() => !!VISTA_GUARDADA?.incluirReservas);
  // Se guarda la vista completa cada vez que cambia algo, y el botón
  // Reset la borra -- igual que los filtros de Transacciones.
  useEffect(() => {
    try {
      localStorage.setItem(
        VISTA_STORAGE_KEY,
        JSON.stringify({
          cuentasSeleccionadas,
          vista,
          vistaMoneda,
          incluirReservas,
          mesActual: format(mesActual, 'yyyy-MM-dd'),
        }),
      );
    } catch {
      // localStorage bloqueado o lleno: la pantalla funciona igual, solo
      // que sin recordar la vista.
    }
  }, [cuentasSeleccionadas, vista, vistaMoneda, incluirReservas, mesActual]);

  const hayVistaPersonalizada = (
    cuentasSeleccionadas.length > 0
    || vista !== 'mes'
    || vistaMoneda !== 'soles'
    || incluirReservas
  );
  const resetearVista = () => {
    setCuentasSeleccionadas([]);
    setVista('mes');
    setVistaMoneda('soles');
    setIncluirReservas(false);
    setMesActual(new Date());
    try {
      localStorage.removeItem(VISTA_STORAGE_KEY);
    } catch {
      // no crítico: el useEffect de arriba vuelve a escribir la clave con
      // los valores ya reseteados.
    }
  };

  const simboloVista = vistaMoneda === 'dolares' ? '$' : 'S/';
  const money = (n) => moneyBase(n, simboloVista);

  useEffect(() => {
    let cancelled = false;
    apiClient.get('/dashboard/accounts')
      .then((res) => {
        if (!cancelled) setCuentas(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setCuentas([]);
      });
    return () => { cancelled = true; };
  }, []);

  const accountIds = cuentasSeleccionadas;

  // OJO: inicioGrid/finGrid deben quedar memoizados por mesActual. Antes se
  // recalculaban como objetos Date NUEVOS en cada render (sin useMemo), lo
  // que hacía que cargarMes (useCallback) cambiara de identidad en cada
  // render -> el useEffect que lo llama se disparaba de nuevo -> setState ->
  // nuevo render -> loop infinito de peticiones a /calendar/data. Eso
  // saturaba el pool de conexiones del backend y lo tiraba a 503 ("no
  // aparecen datos en el calendario"). Con useMemo, la identidad solo
  // cambia cuando mesActual realmente cambia.
  const { inicioGrid, finGrid } = useMemo(() => {
    if (vista === 'dia') {
      return { inicioGrid: mesActual, finGrid: mesActual };
    }
    if (vista === 'semana') {
      return {
        inicioGrid: startOfWeek(mesActual, { weekStartsOn: 1 }),
        finGrid: endOfWeek(mesActual, { weekStartsOn: 1 }),
      };
    }
    const inicioMes = startOfMonth(mesActual);
    const finMes = endOfMonth(mesActual);
    return {
      inicioGrid: startOfWeek(inicioMes, { weekStartsOn: 1 }),
      finGrid: endOfWeek(finMes, { weekStartsOn: 1 }),
    };
  }, [mesActual, vista]);

  const diasGrid = useMemo(
    () => eachDayOfInterval({ start: inicioGrid, end: finGrid }),
    [inicioGrid, finGrid]
  );

  const cargarMes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const start = format(inicioGrid, 'yyyy-MM-dd');
      const end = format(finGrid, 'yyyy-MM-dd');
      const res = await calendarService.getData(start, end, { accountIds, vista: vistaMoneda, incluirReservas });
      const map = {};
      (res.data.dias || []).forEach((d) => { map[d.fecha] = d; });
      setDias(map);
      setBalanceActual(res.data.balance_actual || 0);
      setHoyISO(res.data.hoy || null);
      setSeriesSinCuenta(res.data.series_sin_cuenta || 0);
      setTipoCambio(res.data.tipo_cambio || null);
    } catch (e) {
      setError(isSpanish ? 'No se pudo cargar el calendario.' : 'Could not load the calendar.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inicioGrid, finGrid, accountIds, vistaMoneda, incluirReservas]);

  useEffect(() => {
    cargarMes();
  }, [cargarMes]);

  // En vista "día" el día grande siempre está seleccionado — no tendría
  // sentido navegar a otro día en el grid (solo hay uno) sin que el
  // detalle de abajo lo refleje.
  useEffect(() => {
    if (vista === 'dia') setSelectedDate(mesActual);
  }, [vista, mesActual]);

  const irAnterior = () => setMesActual((prev) => {
    if (vista === 'dia') return subDays(prev, 1);
    if (vista === 'semana') return subWeeks(prev, 1);
    return subMonths(prev, 1);
  });
  const irSiguiente = () => setMesActual((prev) => {
    if (vista === 'dia') return addDays(prev, 1);
    if (vista === 'semana') return addWeeks(prev, 1);
    return addMonths(prev, 1);
  });
  const irHoy = () => { setMesActual(new Date()); setSelectedDate(vista === 'dia' ? new Date() : null); };
  const cambiarVista = (_e, nuevaVista) => { if (nuevaVista) setVista(nuevaVista); };

  const diaData = (date) => dias[format(date, 'yyyy-MM-dd')];

  const selectedData = selectedDate ? diaData(selectedDate) : null;

  // 1 sep 2026 (Augusto: "debe permitir modificar las transacciones en
  // el calendario"): antes el detalle del día era solo lectura. Un
  // movimiento acá puede ser (a) una transacción REAL (m.proyectado ===
  // false, con m.id -- ver calendar.py) o (b) una ocurrencia PROYECTADA
  // de una serie recurrente (m.proyectado === true, sin transacción
  // propia -- solo existe como una fecha calculada de Income/Expense).
  // Editar/omitir una proyectada no toca la serie completa, crea una
  // EXCEPCIÓN puntual para esa fecha (mismo mecanismo que ya usa
  // RecurringPage.js en "Omitir/editar una ocurrencia") -- así el resto
  // de los meses de esa serie no se ven afectados.
  const [editando, setEditando] = useState(null); // { movimiento, fecha } | null
  const [editMonto, setEditMonto] = useState('');
  const [editDescripcion, setEditDescripcion] = useState('');
  // 21 sep 2026 (Augusto: "no permite cambiar fecha y otros"). El
  // diálogo solo dejaba tocar monto y descripción, así que corregir una
  // fecha mal leída por la IA o recategorizar obligaba a salir del
  // calendario, buscar la fila en Transacciones y editarla ahí. Para una
  // transacción real ahora se puede editar todo desde acá.
  //
  // Para una ocurrencia PROYECTADA de una serie recurrente sigue siendo
  // monto y descripción: mover su fecha significaría otra cosa (correr
  // una ocurrencia de la serie), y el modelo de excepciones no guarda
  // una fecha alternativa -- ver RecurringException en el backend.
  const [editFecha, setEditFecha] = useState('');
  const [editCategoria, setEditCategoria] = useState('');
  const [editProveedor, setEditProveedor] = useState('');
  const [editCuenta, setEditCuenta] = useState('');
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [errorEdicion, setErrorEdicion] = useState(null);

  const abrirEditar = (movimiento, fecha) => {
    setEditando({ movimiento, fecha });
    // Se edita sobre el monto ORIGINAL (en su propia moneda), no el ya
    // convertido por el toggle Soles/Dólares -- si no, cada vez que se
    // guardara con vistaMoneda='dolares' se le estaría pisando el monto
    // real en soles con un número convertido.
    setEditMonto(String(movimiento.monto_original ?? movimiento.monto));
    setEditDescripcion(movimiento.descripcion || movimiento.categoria || '');
    setEditFecha(movimiento.fecha || fecha || '');
    setEditCategoria(movimiento.categoria || '');
    setEditProveedor(movimiento.proveedor || '');
    setEditCuenta(movimiento.account_id ?? '');
    setErrorEdicion(null);
  };
  const cerrarEditar = () => {
    setEditando(null);
    setErrorEdicion(null);
  };

  const guardarEdicion = async () => {
    if (!editando) return;
    const monto = parseFloat(editMonto);
    if (!(monto > 0)) return;
    setGuardandoEdicion(true);
    setErrorEdicion(null);
    try {
      const { movimiento, fecha } = editando;
      if (!movimiento.proyectado) {
        // Se manda la fecha a mediodía para que el cambio de zona horaria
        // al serializar no la corra un día hacia atrás -- el bug clásico
        // de guardar "2026-09-03" como 2026-09-02T23:00 en UTC-5.
        await transactionService.update(movimiento.id, {
          monto,
          descripcion: editDescripcion,
          fecha: editFecha ? new Date(`${editFecha}T12:00:00`).toISOString() : undefined,
          categoria: editCategoria || null,
          proveedor: editProveedor || null,
          account_id: editCuenta === '' ? null : Number(editCuenta),
        });
      } else {
        await recurringService.createException(movimiento.recurring_tipo, movimiento.recurring_id, {
          fecha_ocurrencia: fecha,
          accion: 'editar',
          monto_override: monto,
          descripcion_override: editDescripcion || null,
        });
      }
      cerrarEditar();
      await cargarMes();
    } catch (e) {
      setErrorEdicion(e?.response?.data?.detail || (isSpanish ? 'No se pudo guardar el cambio.' : 'Could not save the change.'));
    } finally {
      setGuardandoEdicion(false);
    }
  };

  // Para una transacción real, elimina el registro entero. Para una
  // ocurrencia proyectada no hay nada que "eliminar" (no existe como
  // fila propia) -- se omite solo esa fecha con una excepción, la serie
  // sigue generando el resto de los meses igual.
  const eliminarMovimiento = async (movimiento, fecha) => {
    const mensaje = movimiento.proyectado
      ? (isSpanish
        ? '¿Omitir esta ocurrencia? El resto de los meses de esta serie recurrente no se ven afectados.'
        : "Skip this occurrence? The rest of this recurring series' months are not affected.")
      : (isSpanish ? '¿Eliminar esta transacción?' : 'Delete this transaction?');
    if (!window.confirm(mensaje)) return;
    try {
      if (!movimiento.proyectado) {
        await transactionService.delete(movimiento.id);
      } else {
        await recurringService.createException(movimiento.recurring_tipo, movimiento.recurring_id, {
          fecha_ocurrencia: fecha,
          accion: 'omitir',
        });
      }
      await cargarMes();
    } catch (e) {
      setError(e?.response?.data?.detail || (isSpanish ? 'No se pudo eliminar el movimiento.' : 'Could not delete the movement.'));
    }
  };

  // 21 sep 2026: recalcula el saldo de las cuentas cuyo balance guardado
  // quedó viejo (ver POST /dashboard/accounts/{id}/recalcular-saldo). Es
  // una reconstrucción a partir de los movimientos, no una verdad
  // absoluta -- por eso después se recarga el mes y se deja que Augusto
  // compare contra el banco.
  const [recalculando, setRecalculando] = useState(false);

  const recalcularCuentas = async (ids) => {
    if (!ids.length) return;
    setRecalculando(true);
    try {
      for (const id of ids) {
        await apiClient.post(`/dashboard/accounts/${id}/recalcular-saldo`);
      }
      await cargarMes();
    } catch (e) {
      setError(
        e?.response?.data?.detail
        || (isSpanish ? 'No se pudo recalcular el saldo.' : 'Could not recalculate the balance.')
      );
    } finally {
      setRecalculando(false);
    }
  };

  // 10 ago 2026 (pedido de Augusto): contenido del detalle de un día --
  // saldo de inicio y de fin, alertas de vencimiento y movimientos
  // (ingresos/egresos). Se extrae a una función para reutilizarlo tanto
  // en el diálogo modal (vista Mes/Semana) como en el panel fijo debajo
  // del día grande (vista Día), sin duplicar el JSX.
  const renderDetalleContenido = (data) => {
    // Una cuenta de activo que arranca el día en negativo es la firma de
    // un saldo guardado que quedó viejo -- ver el aviso más abajo. Las
    // tarjetas y préstamos quedan fuera: ahí un negativo es lo normal.
    const cuentasConSaldoSospechoso = (data?.saldos_por_cuenta || []).filter((c) => {
      if (typeof c.saldo_inicio !== 'number' || c.saldo_inicio >= 0) return false;
      const cuenta = cuentas.find((x) => x.id === c.account_id);
      return ['ahorros', 'corriente', 'efectivo'].includes(cuenta?.tipo);
    });
    const recalcularSaldos = () =>
      recalcularCuentas(cuentasConSaldoSospechoso.map((c) => c.account_id));

    return (
    <>
      {(data?.saldo_inicio != null || data?.saldo_fin != null) && (
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          {data?.saldo_inicio != null && (
            <Typography variant="body2" color="text.secondary">
              {isSpanish ? 'Balance de inicio' : 'Starting balance'}: <strong>{money(data.saldo_inicio)}</strong>
            </Typography>
          )}
          {data?.saldo_fin != null && (
            <Typography variant="body2" color="text.secondary">
              {isSpanish ? 'Balance de fin' : 'Ending balance'}: <strong>{money(data.saldo_fin)}</strong>
            </Typography>
          )}
        </Box>
      )}

      {/* 3 sep 2026 (Augusto: "el calendario debe de mostrar los saldos por
          cuenta y el balance total" -> "no quiero los balances fuera,
          necesito los balances dentro de cada día"): el desglose por
          cuenta vivió primero como una franja de chips arriba de la
          grilla (fuera de los días) -- Augusto pidió que viva DENTRO del
          detalle de cada día en cambio. saldos_por_cuenta viene del
          backend ya calculado para ESTE día específico (ver
          calendar.py) -- cada cuenta tiene su propio saldo de fin de día,
          encadenado igual que el total pero por separado. Si se filtró
          por una sola cuenta, la lista trae esa única cuenta (mismo
          número que "Balance de fin" de arriba, se muestra igual para no
          tener un caso especial). */}
      {/* 21 sep 2026 (Augusto: "sigue siendo el balance final de la
          cuenta $0.00 cuando han ingresado $9,124.36").

          Si una cuenta de activo arranca el día en negativo, casi
          siempre es el mismo problema: su saldo guardado quedó viejo
          (los movimientos importados antes de setiembre no lo movían) y
          el encadenado hacia atrás parte de un número que no es real.
          En vez de dejarlo como un misterio, se dice qué pasa y se
          ofrece recalcularlo desde los movimientos. */}
      {cuentasConSaldoSospechoso.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" disabled={recalculando} onClick={recalcularSaldos}>
              {recalculando
                ? (isSpanish ? 'Recalculando…' : 'Recalculating…')
                : (isSpanish ? 'Recalcular' : 'Recalculate')}
            </Button>
          }
        >
          {isSpanish
            ? `El saldo guardado de ${cuentasConSaldoSospechoso.map((c) => c.nombre).join(', ')} no cuadra con sus movimientos, así que el balance de este día sale negativo. Puedo recalcularlo sumando sus movimientos, o corrígelo a mano en Cuentas si sabes el número exacto del banco.`
            : `The stored balance for ${cuentasConSaldoSospechoso.map((c) => c.nombre).join(', ')} doesn't match its transactions, so this day's balance comes out negative. I can recalculate it from the transactions, or fix it by hand under Accounts if you know the exact figure.`}
        </Alert>
      )}

      {data?.saldos_por_cuenta?.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {data.saldos_por_cuenta.map((c) => (
            /* 21 sep 2026 (Augusto: "después de ingresar los incomes,
               debe de mostrar el balance final de cada cuenta que se
               está seleccionando"). Antes el chip solo traía el saldo de
               FIN, así que con varias cuentas no se podía ver qué le
               pasó a cada una ese día -- solo dónde terminó. Ahora
               muestra inicio → fin, y el movimiento del día entre
               paréntesis cuando lo hubo. */
            <Chip
              key={c.account_id}
              size="small"
              variant="outlined"
              sx={{ height: 'auto', py: 0.5, '& .MuiChip-label': { display: 'block', whiteSpace: 'normal' } }}
              label={(() => {
                const inicio = c.saldo_inicio;
                const fin = c.saldo_fin;
                const delta = (typeof inicio === 'number' && typeof fin === 'number') ? fin - inicio : null;
                const nombre = `${c.nombre}${c.entidad ? ` (${c.entidad})` : ''}`;
                // 21 sep 2026: antes del primer movimiento registrado el
                // backend no manda saldo (ver _primer_movimiento en
                // calendar.py). Se dice "sin datos" en vez de imprimir un
                // undefined disfrazado de monto.
                if (typeof fin !== 'number') {
                  return `${nombre}: ${isSpanish ? 'sin datos aún' : 'no data yet'}`;
                }
                if (typeof inicio !== 'number') return `${nombre}: ${money(fin)}`;
                const movimiento = delta && Math.abs(delta) >= 0.005
                  ? ` (${delta > 0 ? '+' : '−'}${money(Math.abs(delta))})`
                  : '';
                return `${nombre}: ${money(inicio)} → ${money(fin)}${movimiento}`;
              })()}
            />
          ))}
        </Box>
      )}

      {(!data || (!data.movimientos.length && !data.alertas_deuda.length)) && (
        <Typography variant="body2" color="text.secondary">
          {isSpanish ? 'Sin movimientos ni alertas para este día.' : 'No movements or alerts for this day.'}
        </Typography>
      )}

      {data?.alertas_deuda?.length > 0 && (
        <List dense>
          {data.alertas_deuda.map((a, idx) => (
            <ListItem key={`alerta-${idx}`}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CreditCardIcon sx={{ color: PINK }} fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={isSpanish ? `Vence: ${a.nombre}` : `Due: ${a.nombre}`}
                secondary={isSpanish ? `Pago mensual estimado: ${money(a.monto)}` : `Estimated monthly payment: ${money(a.monto)}`}
                primaryTypographyProps={{ sx: { color: PINK, fontWeight: 600 } }}
              />
            </ListItem>
          ))}
        </List>
      )}

      {data?.movimientos?.length > 0 && (() => {
        const ingresosDia = data.movimientos.filter((m) => m.tipo === 'ingreso');
        const gastosDia = data.movimientos.filter((m) => m.tipo !== 'ingreso');
        const renderMovimiento = (m, idx) => (
          <ListItem
            key={idx}
            secondaryAction={
              // 30 oct 2026: el pago programado de tarjetas no es una serie
              // recurrente ni una transacción: no hay nada que editar o
              // borrar acá. Se configura en Deudas y se confirma en Revisión.
              m.pago_tarjeta ? null : (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={m.proyectado ? (isSpanish ? 'Editar esta ocurrencia' : 'Edit this occurrence') : (isSpanish ? 'Editar transacción' : 'Edit transaction')}>
                  <IconButton size="small" onClick={() => abrirEditar(m, data.fecha)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={m.proyectado ? (isSpanish ? 'Omitir esta ocurrencia' : 'Skip this occurrence') : (isSpanish ? 'Eliminar transacción' : 'Delete transaction')}>
                  <IconButton size="small" color="error" onClick={() => eliminarMovimiento(m, data.fecha)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              )
            }
          >
            {/* 4 ago 2026 (pedido de Augusto): antes acá iba una flecha
                arriba/abajo genérica según tipo -- redundante porque la
                lista ya está separada en secciones "Ingresos"/"Egresos"
                (ver más abajo). Un emoji por categoría (ver
                utils/categoryEmoji.js) deja identificar de un vistazo
                el TIPO de gasto/ingreso, no solo si fue + o -. */}
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Typography sx={{ fontSize: '1.15rem', lineHeight: 1 }}>
                {getCategoryEmoji(m.categoria)}
              </Typography>
            </ListItemIcon>
            <ListItemText
              sx={{ pr: 9 }}
              primary={`${m.descripcion || m.categoria || (isSpanish ? 'Movimiento' : 'Movement')} — ${money(m.monto)}`}
              secondary={
                m.pago_tarjeta
                  ? (isSpanish
                    ? (m.estimado
                      ? 'Pago de tarjeta al 100% — estimado con la deuda de hoy (el extracto de ese mes aún no existe)'
                      : 'Pago de tarjeta al 100% — Pago total del extracto. Se confirma en Revisión pendiente.')
                    : 'Card payment (100%)')
                  : m.proyectado ? (isSpanish ? 'Proyectado (recurrente)' : 'Projected (recurring)') : (isSpanish ? 'Real' : 'Actual')
              }
            />
            {m.proyectado && (
              <Chip
                label={m.pago_tarjeta && m.estimado ? (isSpanish ? 'Estimado' : 'Estimated') : (isSpanish ? 'Proyectado' : 'Projected')}
                size="small"
                variant="outlined"
                sx={{ ml: 1, mr: m.pago_tarjeta ? 0 : 8 }}
              />
            )}
          </ListItem>
        );
        return (
          <>
            <Divider sx={{ my: 1 }} />
            {ingresosDia.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ color: 'success.main', px: 2, pt: 1 }}>
                  {isSpanish ? 'Ingresos' : 'Income'} ({money(ingresosDia.reduce((acc, m) => acc + m.monto, 0))})
                </Typography>
                <List dense>{ingresosDia.map(renderMovimiento)}</List>
              </>
            )}
            {gastosDia.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ color: 'error.main', px: 2, pt: 1 }}>
                  {isSpanish ? 'Egresos' : 'Expenses'} ({money(gastosDia.reduce((acc, m) => acc + m.monto, 0))})
                </Typography>
                <List dense>{gastosDia.map(renderMovimiento)}</List>
              </>
            )}
          </>
        );
      })()}
    </>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {isSpanish ? 'Calendario de Flujo de Efectivo' : 'Cash Flow Calendar'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isSpanish
          ? 'Transacciones reales, ocurrencias proyectadas de series recurrentes, alertas de vencimiento de deuda y saldo diario/proyectado.'
          : 'Actual transactions, projected occurrences of recurring series, debt due-date alerts, and daily/projected balance.'}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={irAnterior}><ChevronLeftIcon /></IconButton>
          <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center', textTransform: 'capitalize' }}>
            {vista === 'mes' && format(mesActual, 'MMMM yyyy', { locale: dateLocale })}
            {vista === 'semana' && `${format(inicioGrid, 'd MMM', { locale: dateLocale })} – ${format(finGrid, 'd MMM yyyy', { locale: dateLocale })}`}
            {vista === 'dia' && format(mesActual, isSpanish ? "EEEE d 'de' MMMM yyyy" : 'EEEE, MMMM d, yyyy', { locale: dateLocale })}
          </Typography>
          <IconButton onClick={irSiguiente}><ChevronRightIcon /></IconButton>
          <Tooltip title={isSpanish ? 'Ir a hoy' : 'Go to today'}>
            <IconButton onClick={irHoy} size="small" sx={{ ml: 1 }}><TodayIcon fontSize="small" /></IconButton>
          </Tooltip>
          <ToggleButtonGroup value={vista} exclusive size="small" onChange={cambiarVista} sx={{ ml: 2 }}>
            <ToggleButton value="mes">{isSpanish ? 'Mes' : 'Month'}</ToggleButton>
            <ToggleButton value="semana">{isSpanish ? 'Semana' : 'Week'}</ToggleButton>
            <ToggleButton value="dia">{isSpanish ? 'Día' : 'Day'}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* 21 sep 2026, segunda vuelta (Augusto: "siguen montadas las
              letras"). El primer intento fue `shrink` + `notched` sobre
              el label flotante; no alcanzó. En vez de seguir adivinando
              qué lo pisa, se elimina el label flotante: el texto del
              propio control ya dice lo que hay que saber ("Todas las
              cuentas", "2 cuentas"), así que un rótulo encima era
              redundante además de frágil. Sin InputLabel no existe el
              problema de dónde se posa.
              `alignSelf: flex-start` evita que el texto de ayuda de abajo
              empuje al resto de la fila. */}
          <FormControl size="small" sx={{ minWidth: 240, alignSelf: 'flex-start' }}>
            <Select
              multiple
              displayEmpty
              value={cuentasSeleccionadas}
              onChange={(e) => {
                setCuentasSeleccionadas(e.target.value);
                setSelectedDate(vista === 'dia' ? mesActual : null);
                setDialogoAbierto(false);
              }}
              renderValue={(sel) => {
                if (sel.length === 0) return isSpanish ? 'Todas las cuentas' : 'All accounts';
                if (sel.length === 1) {
                  const c = cuentas.find((x) => x.id === sel[0]);
                  return c ? c.nombre : '1';
                }
                return isSpanish ? `${sel.length} cuentas` : `${sel.length} accounts`;
              }}
            >
              {cuentas.length === 0 && (
                <MenuItem disabled value="__no_cuentas">{isSpanish ? 'Sin cuentas registradas' : 'No accounts registered'}</MenuItem>
              )}
              {cuentas.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <Checkbox size="small" checked={cuentasSeleccionadas.includes(c.id)} />
                  {c.es_reserva ? '🔒 ' : ''}{c.nombre}{c.entidad ? ` (${c.entidad})` : ''}{c.tipo ? ` — ${c.tipo}` : ''}
                </MenuItem>
              ))}
            </Select>
            {/* Sin cuentas elegidas se muestran todas -- decirlo evita
                que "ninguna seleccionada" se lea como "no hay nada". */}
            <FormHelperText>
              {cuentasSeleccionadas.length === 0
                ? (isSpanish ? 'Sin elegir ninguna, se muestran todas' : 'With none selected, all are shown')
                : (isSpanish ? 'Los saldos suman solo las elegidas' : 'Balances add up only the selected ones')}
            </FormHelperText>
          </FormControl>
          {/* 21 sep 2026 (Augusto: "una vez más el balance en la
              transacción no es igual al que muestra el calendario").
              El calendario muestra saldos de una FECHA y, si estás en
              vista Soles o Dólares, CONVERTIDOS al tipo de cambio del
              día. El formulario de transacción muestra el saldo de HOY de
              UNA cuenta, en su propia moneda. Son dos cosas distintas y
              parecían la misma. Acá va el número tal cual está guardado,
              sin convertir y sin encadenar: es el que tiene que coincidir
              con el del formulario. */}
          {cuentasSeleccionadas.length > 0 && seriesSinCuenta > 0 && (
            <Alert
              severity="warning"
              sx={{ width: '100%', mt: 1 }}
              action={
                <Button color="inherit" size="small" onClick={() => navigate('/recurring')}>
                  {isSpanish ? 'Asignarles cuenta' : 'Assign accounts'}
                </Button>
              }
            >
              {isSpanish
                ? `${seriesSinCuenta} serie(s) recurrente(s) no tienen cuenta asignada, así que no aparecen mientras filtres por cuenta. Asígnales una en Recurrentes y se proyectarán acá.`
                : `${seriesSinCuenta} recurring series have no account assigned, so they do not show while you filter by account. Assign one under Recurring and they will project here.`}
            </Alert>
          )}
          {cuentasSeleccionadas.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ width: '100%', display: 'block', mt: -1 }}>
              {isSpanish ? 'Saldo hoy (sin convertir): ' : 'Balance today (unconverted): '}
              {cuentasSeleccionadas
                .map((id) => cuentas.find((c) => c.id === id))
                .filter(Boolean)
                .map((c) => `${c.nombre} ${c.moneda === 'USD' ? '$' : 'S/'} ${Number(c.saldo || 0).toFixed(2)}`)
                .join(' · ')}
            </Typography>
          )}
          {/* 4 sep 2026 (Augusto: "cuentas provisiones o ahorros
              intocables... no incluirse en el calendario... a menos
              indicado por el usuario"): este toggle solo importa cuando se
              está viendo el agregado "todas las cuentas" -- si ya se
              seleccionó una cuenta puntual arriba (sea o no reserva),
              siempre se muestra igual, con o sin este switch. */}
          {cuentasSeleccionadas.length === 0 && cuentas.some((c) => c.es_reserva) && (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch
                  size="small"
                  checked={incluirReservas}
                  onChange={(e) => setIncluirReservas(e.target.checked)}
                />
              }
              label={<Typography variant="body2">{isSpanish ? 'Incluir reservas 🔒' : 'Include reserves 🔒'}</Typography>}
            />
          )}
          {/* 1 sep 2026 (Augusto: "necesito la opción de toggle de mostrar
              solo en soles o solo en dólares"): a diferencia de
              Transacciones/Presupuesto/Recurrentes (que nunca convierten
              soles y dólares entre sí a propósito), acá el pedido es
              justo lo contrario -- todo el calendario en UNA sola moneda,
              usando el tipo de cambio del día. */}
          <ToggleButtonGroup
            value={vistaMoneda}
            exclusive
            size="small"
            onChange={(_e, nuevaVista) => { if (nuevaVista) setVistaMoneda(nuevaVista); }}
          >
            <ToggleButton value="soles">{isSpanish ? 'Soles' : 'Soles'}</ToggleButton>
            <ToggleButton value="dolares">{isSpanish ? 'Dólares' : 'Dollars'}</ToggleButton>
          </ToggleButtonGroup>
          {/* 21 sep 2026 (Augusto: "las cuentas y moneda y fecha
              seleccionada debe de quedar registrada hasta que el usuario
              haga un Reset"). El botón solo aparece cuando hay algo que
              resetear -- si no, es un botón que no hace nada ocupando
              lugar en la barra. */}
          {hayVistaPersonalizada && (
            <Button size="small" onClick={resetearVista}>
              {isSpanish ? 'Reset' : 'Reset'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Tipo de cambio usado para convertir -- Augusto: "Muestra el tipo
          de cambio y la fuente". Si tipoCambio es null (el BCRP no
          respondió y tampoco había nada cacheado en el server), se avisa
          en vez de mostrar un número inventado. */}
      {tipoCambio ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {isSpanish
            ? `Tipo de cambio del ${tipoCambio.fecha} (compra S/${tipoCambio.compra} · venta S/${tipoCambio.venta} · promedio usado S/${tipoCambio.promedio} por US$) · Fuente: ${tipoCambio.fuente}`
            : `Exchange rate for ${tipoCambio.fecha} (buy S/${tipoCambio.compra} · sell S/${tipoCambio.venta} · average used S/${tipoCambio.promedio} per US$) · Source: ${tipoCambio.fuente}`}
        </Typography>
      ) : (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 2 }}>
          {isSpanish
            ? 'No se pudo obtener el tipo de cambio del día -- los montos en otra moneda pueden no estar convertidos.'
            : "Could not get today's exchange rate -- amounts in another currency may not be converted."}
        </Typography>
      )}

      {error && (
        <Box sx={{ my: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography variant="body2" color="error.main">{error}</Typography>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 1, mb: 3, opacity: loading ? 0.6 : 1 }}>
        <Grid container>
          {vista !== 'dia' && diasSemanaAbrev.map((d) => (
            <Grid item xs={12 / 7} key={d}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', fontWeight: 600, py: 0.5 }}>
                {d}
              </Typography>
            </Grid>
          ))}

          {diasGrid.map((date) => {
            const data = diaData(date);
            const enMes = vista === 'mes' ? isSameMonth(date, mesActual) : true;
            const esHoy = hoyISO ? format(date, 'yyyy-MM-dd') === hoyISO : isToday(date);
            const seleccionado = selectedDate && isSameDay(date, selectedDate);
            const tieneIngreso = data && data.ingresos > 0;
            const tieneGasto = data && data.gastos > 0;
            const tieneAlerta = data && data.alertas_deuda?.length > 0;

            // 9 sep 2026 (Augusto: "el calendario tiene que mostrar solo
            // total ingresos, total egresos y el balance por día. Solo se
            // puede ver el detalle ingresando a cada día o hovering over
            // el día"): antes la alerta de vencimiento de deuda vivía
            // siempre visible como una fila más dentro de la celda -- eso
            // es "detalle", así que se saca de la vista por defecto. Entrar
            // al día (clic, ya existía) sigue abriendo el detalle completo;
            // ahora un hover sobre la celda también lo muestra, vía
            // Tooltip, sin agregar nada visible a la celda en sí.
            const celda = (
              <Box
                onClick={() => {
                  setSelectedDate(date);
                  if (vista !== 'dia') setDialogoAbierto(true);
                }}
                sx={{
                  m: 0.3,
                  p: vista === 'dia' ? 2 : 0.7,
                  minHeight: vista === 'dia' ? 160 : 92,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: seleccionado ? 'primary.main' : 'divider',
                  bgcolor: esHoy ? 'primary.50' : 'background.paper',
                  opacity: enMes ? 1 : 0.4,
                  '&:hover': { borderColor: 'primary.light' },
                }}
              >
                <Typography variant={vista === 'dia' ? 'subtitle1' : 'caption'} sx={{ fontWeight: esHoy ? 700 : 400, textTransform: vista === 'dia' ? 'capitalize' : 'none' }}>
                  {vista === 'dia' ? format(date, isSpanish ? "EEEE d 'de' MMMM" : 'EEEE, MMMM d', { locale: dateLocale }) : format(date, 'd')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, mt: 0.3 }}>
                  {tieneIngreso && (
                    <Typography variant="caption" sx={{ color: 'success.main', fontSize: '0.65rem' }}>
                      +{money(data.ingresos)}
                    </Typography>
                  )}
                  {tieneGasto && (
                    <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.65rem' }}>
                      -{money(data.gastos)}
                    </Typography>
                  )}
                  {data?.saldo_fin != null && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        color: data.saldo_fin < 0 ? 'error.main' : 'text.secondary',
                        borderTop: '1px dashed',
                        borderColor: 'divider',
                        pt: 0.2,
                        mt: 0.2,
                      }}
                    >
                      {isSpanish ? 'Saldo' : 'Balance'}: {money(data.saldo_fin)}
                    </Typography>
                  )}
                </Box>
              </Box>
            );

            return (
              <Grid item xs={vista === 'dia' ? 12 : 12 / 7} key={date.toISOString()}>
                {tieneAlerta ? (
                  <Tooltip
                    title={data.alertas_deuda.map((a) => `${a.nombre}: ${money(a.monto)}`).join(' · ')}
                    arrow
                  >
                    {celda}
                  </Tooltip>
                ) : celda}
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Detalle del día seleccionado -- en vista Día queda fijo debajo del
          día grande (ya no hay una grilla con más días para "abrir" con un
          modal). En vista Mes/Semana, el detalle vive en el diálogo de
          abajo, que se abre al hacer clic en un día. */}
      {vista === 'dia' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          {!selectedDate ? (
            <Typography variant="body2" color="text.secondary">
              {isSpanish ? 'Selecciona un día en el calendario para ver el detalle.' : 'Select a day on the calendar to see the detail.'}
            </Typography>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 1, textTransform: 'capitalize' }}>
                {format(selectedDate, isSpanish ? "EEEE d 'de' MMMM" : 'EEEE, MMMM d', { locale: dateLocale })}
              </Typography>
              {renderDetalleContenido(selectedData)}
            </>
          )}
        </Paper>
      )}

      {/* 10 ago 2026 (pedido de Augusto): clic en un día del calendario
          "abre el día" en este diálogo -- muestra sus transacciones y el
          balance de inicio y de fin, sin tener que bajar a un panel al
          final de la página. */}
      <Dialog
        open={dialogoAbierto && vista !== 'dia'}
        onClose={() => setDialogoAbierto(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', textTransform: 'capitalize' }}>
          {selectedDate ? format(selectedDate, isSpanish ? "EEEE d 'de' MMMM" : 'EEEE, MMMM d', { locale: dateLocale }) : ''}
          <IconButton onClick={() => setDialogoAbierto(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {renderDetalleContenido(selectedData)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoAbierto(false)}>{isSpanish ? 'Cerrar' : 'Close'}</Button>
        </DialogActions>
      </Dialog>

      {/* 1 sep 2026 (Augusto: "debe permitir modificar las transacciones
          en el calendario"): diálogo chico de edición -- solo monto y
          descripción (mismo alcance que el resto del detalle del día, no
          se pretende reemplazar el formulario completo de Transacciones).
          Ver abrirEditar/guardarEdicion. */}
      <Dialog open={!!editando} onClose={cerrarEditar} fullWidth maxWidth="xs">
        <DialogTitle>
          {editando?.movimiento?.proyectado
            ? (isSpanish ? 'Editar esta ocurrencia' : 'Edit this occurrence')
            : (isSpanish ? 'Editar transacción' : 'Edit transaction')}
        </DialogTitle>
        <DialogContent>
          {editando?.movimiento?.proyectado && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isSpanish ? (
                <>Es parte de una serie recurrente -- este cambio solo afecta a la ocurrencia del{' '}{editando.fecha}, el resto de los meses de la serie sigue igual.</>
              ) : (
                <>It's part of a recurring series -- this change only affects the occurrence on{' '}{editando.fecha}, the rest of the series' months stay the same.</>
              )}
            </Typography>
          )}
          {errorEdicion && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorEdicion}</Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={`${isSpanish ? 'Monto' : 'Amount'}${editando?.movimiento?.moneda_original ? ` (${editando.movimiento.moneda_original})` : ''}`}
            type="number"
            fullWidth
            value={editMonto}
            onChange={(e) => setEditMonto(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label={isSpanish ? 'Descripción' : 'Description'}
            fullWidth
            value={editDescripcion}
            onChange={(e) => setEditDescripcion(e.target.value)}
          />
          {/* 21 sep 2026 (Augusto: "no permite cambiar fecha y otros").
              Solo para transacciones reales: en una ocurrencia proyectada
              de una serie recurrente, mover la fecha significaría correr
              una ocurrencia de la serie, y el modelo de excepciones no
              guarda una fecha alternativa (ver RecurringException). */}
          {!editando?.movimiento?.proyectado && (
            <>
              <TextField
                margin="dense"
                label={isSpanish ? 'Fecha' : 'Date'}
                type="date"
                fullWidth
                value={editFecha}
                onChange={(e) => setEditFecha(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
              />
              <TextField
                margin="dense"
                label={isSpanish ? 'Categoría' : 'Category'}
                fullWidth
                value={editCategoria}
                onChange={(e) => setEditCategoria(e.target.value)}
                sx={{ mt: 2 }}
              />
              <TextField
                margin="dense"
                label={isSpanish ? 'Proveedor' : 'Merchant'}
                fullWidth
                value={editProveedor}
                onChange={(e) => setEditProveedor(e.target.value)}
                sx={{ mt: 2 }}
              />
              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel id="edit-cuenta-label">{isSpanish ? 'Cuenta' : 'Account'}</InputLabel>
                <Select
                  labelId="edit-cuenta-label"
                  label={isSpanish ? 'Cuenta' : 'Account'}
                  value={editCuenta}
                  onChange={(e) => setEditCuenta(e.target.value)}
                >
                  <MenuItem value="">{isSpanish ? 'Sin cuenta' : 'No account'}</MenuItem>
                  {cuentas.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.nombre}{c.moneda ? ` (${c.moneda})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Las dos mitades de una transferencia están enlazadas: si
                  se corre una sola, el movimiento queda partido entre dos
                  días y el saldo de cada cuenta deja de cuadrar. */}
              {editando?.movimiento?.es_transferencia && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {isSpanish
                    ? 'Es un lado de una transferencia. Si cambias la fecha o la cuenta, acuérdate de ajustar también el otro lado.'
                    : 'This is one side of a transfer. If you change the date or account, remember to adjust the other side too.'}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarEditar} disabled={guardandoEdicion}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button
            variant="contained"
            onClick={guardarEdicion}
            disabled={guardandoEdicion || !(parseFloat(editMonto) > 0)}
          >
            {isSpanish ? 'Guardar' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CalendarPage;
